import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Play,
  Pause,
  Shuffle,
  Copy,
  Pencil,
  Plus,
  Scissors,
  BookOpen,
  X,
  Gauge,
  Timer,
  Trash2,
  Eye,
  Bell,
  BellOff,
  Minus,
  Bookmark,
} from "lucide-react";
import ArticleCard from "../components/ArticleCard";
import Controls from "../components/Controls";
import { articlesAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";

import useScreenWakeLock from "../hooks/useScreenWakeLock";
import useStudyTimerExtension from "../hooks/useStudyTimerExtension";
import { useReading } from "../context/ReadingContext";
import Spinner from "../components/Spinner";
import StudyLayout from "../components/StudyLayout";
import NavigationButtons from "../components/NavigationButtons";
import { CONTENT_LIMITS } from "../constants/contentLimits";
import Breadcrumbs from "../components/Breadcrumbs";
import "./StudyNotes.css";

const StudyNotes = () => {
  useScreenWakeLock(true);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawFolderFromUrl = searchParams.get("folder");
  const folderFromUrl =
    rawFolderFromUrl &&
    rawFolderFromUrl !== "null" &&
    rawFolderFromUrl !== "undefined"
      ? rawFolderFromUrl
      : "";
  const typeFromUrl = searchParams.get("type");
  const lessonIdFromUrl = searchParams.get("lessonId");
  const returnIndex = searchParams.get("index");
  const bookmarkNoteId = searchParams.get("bookmarkNoteId");
  const skipFolderLoad = searchParams.get("skipFolderLoad");
  const [articles, setArticles] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(
    skipFolderLoad ? null : folderFromUrl || null,
  );
  const [jumpIndex, setJumpIndex] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalArticles, setTotalArticles] = useState(0);
  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(false);
  const [readingModeEnabled, setReadingModeEnabled] = useState(false);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showDurationInfo, setShowDurationInfo] = useState(false);
  const [targetHours, setTargetHours] = useState("");
  const [targetMinutes, setTargetMinutes] = useState("");
  const [showTimerMenu, setShowTimerMenu] = useState(false);
  const [timerHours, setTimerHours] = useState("");
  const [timerMinutes, setTimerMinutes] = useState("");
  const [timerEndTime, setTimerEndTime] = useState(null);
  const [timerDisplay, setTimerDisplay] = useState("");
  useStudyTimerExtension(setTimerEndTime, setTimerDisplay);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSplitMode, setIsSplitMode] = useState(false);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [newArticleBody, setNewArticleBody] = useState("");
  const [originalArticle, setOriginalArticle] = useState(null);
  const [chimeEnabled, setChimeEnabled] = useState(() => {
    const saved = localStorage.getItem("chimeEnabled");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [progressLineEnabled, setProgressLineEnabled] = useState(() => {
    const saved = localStorage.getItem("progressLineEnabled");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [bookmark, setBookmark] = useState(null);
  const [isBookmarkMode, setIsBookmarkMode] = useState(false);
  const [loadingBookmark, setLoadingBookmark] = useState(false);
  const cursorTimeoutRef = React.useRef(null);
  const lastChimeTimeRef = React.useRef(0);
  const editTextareaRef = React.useRef(null);
  const bookmarkActionRef = React.useRef(null);
  const { addToast } = useToasts();
  const {
    readingModeEnabled: globalReadingMode,
    toggleReadingMode,
    speedMultiplier,
    setSpeedMultiplier,
  } = useReading();
  const bookmarkScope = lessonIdFromUrl
    ? `studynotes:${lessonIdFromUrl}`
    : "studynotes";

  const buildLessonPath = React.useCallback(
    (lessonId, extraParams = {}) => {
      const params = new URLSearchParams();

      if (selectedFolder) {
        params.set("folder", selectedFolder);
      }

      Object.entries(extraParams).forEach(([key, value]) => {
        if (
          value === undefined ||
          value === null ||
          value === false ||
          value === ""
        ) {
          return;
        }

        params.set(key, String(value));
      });

      const query = params.toString();
      return query ? `/view/lesson/${lessonId}?${query}` : `/view/lesson/${lessonId}`;
    },
    [selectedFolder],
  );

  const getArticleId = React.useCallback((articleOrId) => {
    if (!articleOrId) return "";
    return typeof articleOrId === "object" ? articleOrId._id : articleOrId;
  }, []);

  const buildNotesPathForBookmarkedNote = React.useCallback(
    (note) => {
      const params = new URLSearchParams();
      const noteLessonId = getArticleId(note.lessonId);

      if (note.title) {
        params.set("folder", note.title);
      }

      if (noteLessonId) {
        params.set("type", "note");
        params.set("lessonId", noteLessonId);
      }

      params.set("bookmarkNoteId", note._id);

      return `/study/notes?${params.toString()}`;
    },
    [getArticleId],
  );


  React.useEffect(() => {
    setReadingModeEnabled(globalReadingMode);
  }, [globalReadingMode]);

  const loadBookmark = React.useCallback(async () => {
    try {
      const response = await articlesAPI.getMyArticles("bookmark");
      const bm = (response.data.articles || []).find(
        (b) => b.chapter === bookmarkScope,
      );
      if (bm) {
        setBookmark({ articleId: bm.body, id: bm._id });
      } else {
        setBookmark(null);
      }
    } catch {
      setBookmark(null);
    }
  }, [bookmarkScope]);

  const handleBookmarkOutsideCurrentView = React.useCallback(async () => {
    if (!bookmark || loadingBookmark) return;

    setLoadingBookmark(true);
    try {
      const response = await articlesAPI.getArticleById(bookmark.articleId);
      const bookmarkedNote = response.data.article;

      if (!bookmarkedNote || bookmarkedNote.contentType !== "note") {
        throw new Error("Bookmark target is not a note");
      }

      setIsBookmarkMode(false);
      addToast("Opening bookmarked note", "success-info");
      navigate(buildNotesPathForBookmarkedNote(bookmarkedNote));
    } catch {
      try {
        await articlesAPI.deleteArticle(bookmark.id);
      } catch {
        // The bookmark is already stale from the user's perspective.
      }

      setBookmark(null);
      setIsBookmarkMode(false);
      addToast("Bookmark no longer exists", "error-warning");
    } finally {
      setLoadingBookmark(false);
    }
  }, [
    addToast,
    bookmark,
    buildNotesPathForBookmarkedNote,
    loadingBookmark,
    navigate,
  ]);

  useEffect(() => {
    loadBookmark();
  }, [loadBookmark]);

  useEffect(() => {
    if (!isBookmarkMode) return;

    const handlePointerDown = (event) => {
      if (bookmarkActionRef.current?.contains(event.target)) return;
      setIsBookmarkMode(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isBookmarkMode]);

  const handleBookmarkClick = async () => {
    if (loadingBookmark || totalArticles === 0) return;
    if (bookmark) {
      const idx = articles.findIndex(a => a._id === bookmark.articleId);
      if (idx >= 0) {
        setCurrentIndex(idx);
        setIsPaused(false);
        addToast(`Bookmark: ${idx + 1} / ${totalArticles}`, "success-info");
      } else {
        await handleBookmarkOutsideCurrentView();
      }
    } else {
      addToast("No bookmark", "error-warning");
      setIsBookmarkMode(true);
    }
  };

  const handleCreateBookmark = async () => {
    if (loadingBookmark || totalArticles === 0) return;
    const current = articles[currentIndex];
    if (!current) return;
    setLoadingBookmark(true);
    try {
      const existing = await articlesAPI.getMyArticles("bookmark");
      const oldBm = (existing.data.articles || []).find(
        (b) => b.chapter === bookmarkScope,
      );
      if (oldBm) await articlesAPI.deleteArticle(oldBm._id);
      const response = await articlesAPI.createArticle({
        contentType: "bookmark",
        body: current._id,
        chapter: bookmarkScope,
        title: "studynotes-bookmark",
        ...(lessonIdFromUrl ? { lessonId: lessonIdFromUrl } : {}),
      });
      setBookmark({ articleId: current._id, id: response.data._id });
      setIsBookmarkMode(false);
      addToast("Bookmark created", "success-info");
    } catch {
      addToast("Failed to create bookmark", "error-warning");
    } finally {
      setLoadingBookmark(false);
    }
  };

  const handleDeleteBookmark = async () => {
    if (!bookmark || loadingBookmark) return;
    setLoadingBookmark(true);
    try {
      await articlesAPI.deleteArticle(bookmark.id);
      setBookmark(null);
      addToast("Bookmark deleted", "success-info");
    } catch {
      addToast("Failed to delete bookmark", "error-warning");
    } finally {
      setLoadingBookmark(false);
    }
  };

  const loadMyArticles = React.useCallback(async () => {
    try {
      let response;
      if (lessonIdFromUrl && typeFromUrl) {
        response = await articlesAPI.getLessonItems(lessonIdFromUrl, typeFromUrl);
      } else if (selectedFolder) {
        response = await articlesAPI.getArticlesByFolder(selectedFolder);
      } else {
        response = await articlesAPI.getMyArticles();
      }
      let myArticles = response.data.articles;

      // When no folder is selected, only show notes
      if (!selectedFolder) {
        myArticles = myArticles.filter((a) => a.contentType === "note");
      }

      // Filter by type if specified
      if (typeFromUrl) {
        myArticles = myArticles.filter((a) => a.contentType === typeFromUrl);
      }

      // Filter by lessonId if specified.
      if (lessonIdFromUrl) {
        myArticles = myArticles.filter(
          (a) =>
            a.lessonId?._id === lessonIdFromUrl ||
            a.lessonId === lessonIdFromUrl,
        );
      }

      // Sort lesson notes by order field.
      if (typeFromUrl === "note") {
        myArticles = myArticles.sort((a, b) => {
          // Items with order field come first, sorted by order
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          // Items without order go to the end
          if (a.order !== undefined) return -1;
          if (b.order !== undefined) return 1;
          return 0;
        });
      }

      setArticles(
        myArticles.map((article) => ({
          title: article.title || "Untitled Article",
          body: article.body || "",
          ...article,
        })),
      );
      setTotalArticles(myArticles.length);

      setLoading(false);
      setInitialLoadDone(true);
    } catch {
      addToast("Failed to load articles", "error-warning");
      setLoading(false);
    }
  }, [addToast, selectedFolder, typeFromUrl, lessonIdFromUrl]);

  // Exit edit/split mode on unmount or navigation
  useEffect(() => {
    return () => {
      if (isEditMode || isSplitMode) {
        // Don't reload on unmount
      }
    };
  }, [isEditMode, isSplitMode]);

  // ESC key to exit reading mode or page
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape") {
        if (globalReadingMode) {
          e.stopPropagation();
          e.preventDefault();
          toggleReadingMode();
          if (document.fullscreenElement) {
            document.exitFullscreen();
          }
          addToast("Reading mode OFF", "success-info");
          return;
        }

        // Otherwise, let ExitButton handler manage Escape
      }
    };

    window.addEventListener("keydown", handleEscKey, true);
    return () => window.removeEventListener("keydown", handleEscKey, true);
  }, [globalReadingMode, toggleReadingMode, addToast]);

  useEffect(() => {
    if (folderFromUrl) {
      setSelectedFolder(folderFromUrl);
    } else {
      setSelectedFolder(null);
    }
  }, [folderFromUrl]);

  // Curata parametrii de jump din URL dupa ce au fost folositi
  useEffect(() => {
    if ((returnIndex !== null || bookmarkNoteId) && !loading) {
      const params = new URLSearchParams();

      if (selectedFolder) {
        params.set("folder", selectedFolder);
      }

      if (typeFromUrl) {
        params.set("type", typeFromUrl);
      }

      if (lessonIdFromUrl) {
        params.set("lessonId", lessonIdFromUrl);
      }

      const query = params.toString();
      const newUrl = query ? `/study/notes?${query}` : "/study/notes";
      window.history.replaceState({}, "", newUrl);
    }
  }, [bookmarkNoteId, lessonIdFromUrl, returnIndex, loading, selectedFolder, typeFromUrl]);

  useEffect(() => {
    loadMyArticles();
    const savedTimer = localStorage.getItem("studyTimer");
    if (savedTimer) {
      const endTime = parseInt(savedTimer);
      setTimerEndTime(endTime);
    }

    const handleStorageChange = (e) => {
      if (e.key === "token" && !e.newValue) {
        setTimerEndTime(null);
        setTimerDisplay("");
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [loadMyArticles, addToast]);

  // Set index after articles are loaded
  useEffect(() => {
    if (!initialLoadDone || totalArticles === 0) return;

    const savedState = localStorage.getItem("studyNotesState");
    if (bookmarkNoteId) {
      const idx = articles.findIndex((article) => article._id === bookmarkNoteId);
      if (idx >= 0) {
        setCurrentIndex(idx);
        addToast(`Bookmark: ${idx + 1} / ${totalArticles}`, "success-info");
      } else {
        addToast("Bookmarked note not in this notes view", "error-warning");
      }
    } else if (returnIndex !== null) {
      const idx = parseInt(returnIndex);
      if (idx >= 0 && idx < totalArticles) {
        setCurrentIndex(idx);
      }
    } else if (savedState) {
      const { folder, index, type } = JSON.parse(savedState);
      if (
        folder === selectedFolder &&
        type === typeFromUrl &&
        index >= 0 &&
        index < totalArticles
      ) {
        setCurrentIndex(index);
      }
    }
  }, [
    initialLoadDone,
    totalArticles,
    articles,
    bookmarkNoteId,
    addToast,
    returnIndex,
    selectedFolder,
    typeFromUrl,
  ]);

  // Save state to localStorage whenever currentIndex or selectedFolder changes (after initial load)
  useEffect(() => {
    if (initialLoadDone && totalArticles > 0) {
      localStorage.setItem(
        "studyNotesState",
        JSON.stringify({
          folder: selectedFolder,
          index: currentIndex,
          type: typeFromUrl,
        }),
      );
    }
  }, [
    currentIndex,
    selectedFolder,
    initialLoadDone,
    totalArticles,
    typeFromUrl,
  ]);

  const handleSplitToggle = React.useCallback(() => {
    if (isSplitMode) {
      setIsSplitMode(false);
      setNewArticleBody("");
      setOriginalArticle(null);
      setIsPaused(false);
      addToast("Split cancelled", "success-info");
    } else {
      setIsEditMode(false);
      setIsDeleteMode(false);
      setOriginalArticle({ ...articles[currentIndex] });
      setIsSplitMode(true);
      setNewArticleBody("");
      setIsPaused(true);
      addToast("Split mode", "success-info");
    }
  }, [isSplitMode, articles, currentIndex, addToast]);

  const handleDeleteToggle = React.useCallback(() => {
    if (isDeleteMode) {
      setIsDeleteMode(false);
      setIsPaused(false);
      addToast("Delete cancelled", "success-info");
    } else {
      setIsEditMode(false);
      setIsSplitMode(false);
      setIsDeleteMode(true);
      setIsPaused(true);
      addToast("Delete mode", "success-info");
    }
  }, [isDeleteMode, addToast]);

  const handleEditToggle = React.useCallback(() => {
    if (isEditMode) {
      if (originalArticle) {
        const updatedArticles = [...articles];
        updatedArticles[currentIndex] = { ...originalArticle };
        setArticles(updatedArticles);
      }
      setIsEditMode(false);
      setOriginalArticle(null);
      setIsPaused(false);
      addToast("Editing cancelled", "success-info");
    } else {
      setIsSplitMode(false);
      setIsDeleteMode(false);
      setOriginalArticle({ ...articles[currentIndex] });
      setIsEditMode(true);
      setIsPaused(true);
      addToast("Editing mode", "success-info");
    }
  }, [isEditMode, articles, currentIndex, addToast, originalArticle]);

  useEffect(() => {
    if (!isEditMode) {
      window.dispatchEvent(new Event("study-timer-recheck"));
    }
  }, [isEditMode]);

  const handlePrevious = React.useCallback(() => {
    if (totalArticles === 0) return;
    setIsBookmarkMode(false);
    if (isEditMode || isSplitMode || isDeleteMode) {
      isEditMode
        ? handleEditToggle()
        : isSplitMode
          ? handleSplitToggle()
          : handleDeleteToggle();
      return;
    }

    const newIndex = currentIndex > 0 ? currentIndex - 1 : totalArticles - 1;
    if (newIndex === currentIndex) {
      setResetTrigger((prev) => !prev);
    } else {
      setCurrentIndex(newIndex);
    }
    setIsPaused(false);
  }, [
    currentIndex,
    totalArticles,
    isEditMode,
    isSplitMode,
    isDeleteMode,
    handleEditToggle,
    handleSplitToggle,
    handleDeleteToggle,
  ]);

  const handleNext = React.useCallback(
    (playChime = true) => {
      if (totalArticles === 0) return;
      setIsBookmarkMode(false);
      if (isEditMode || isSplitMode || isDeleteMode) {
        isEditMode
          ? handleEditToggle()
          : isSplitMode
            ? handleSplitToggle()
            : handleDeleteToggle();
        return;
      }

      // Play pleasant chime sound only if requested and cooldown passed
      if (chimeEnabled && playChime) {
        const currentTime = Date.now();
        const timeSinceLastChime = currentTime - lastChimeTimeRef.current;

        if (timeSinceLastChime >= 1000) {
          lastChimeTimeRef.current = currentTime;

          const audioContext = new (
            window.AudioContext || window.webkitAudioContext
          )();
          const now = audioContext.currentTime;

          // Soft single note (C5) - most gentle
          [523.25].forEach((freq) => {
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = freq;
            oscillator.type = "sine";

            gainNode.gain.setValueAtTime(0, now);
            gainNode.gain.linearRampToValueAtTime(0.08, now + 0.02);
            gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.8);

            oscillator.start(now);
            oscillator.stop(now + 0.8);
          });
        }
      }

      const newIndex = currentIndex < totalArticles - 1 ? currentIndex + 1 : 0;
      if (newIndex === currentIndex) {
        setResetTrigger((prev) => !prev);
      } else {
        setCurrentIndex(newIndex);
      }
      setIsPaused(false);
    },
    [
      currentIndex,
      totalArticles,
      isEditMode,
      isSplitMode,
      isDeleteMode,
      chimeEnabled,
      handleEditToggle,
      handleSplitToggle,
      handleDeleteToggle,
    ],
  );

  const togglePause = React.useCallback(() => {
    if (isEditMode || isSplitMode || isDeleteMode) {
      isEditMode
        ? handleEditToggle()
        : isSplitMode
          ? handleSplitToggle()
          : handleDeleteToggle();
      return;
    }
    setIsPaused((prev) => !prev);
  }, [
    isEditMode,
    isSplitMode,
    isDeleteMode,
    handleEditToggle,
    handleSplitToggle,
    handleDeleteToggle,
  ]);

  const handleRandom = React.useCallback(() => {
    if (totalArticles === 0) return;
    setIsBookmarkMode(false);
    if (isEditMode || isSplitMode || isDeleteMode) {
      isEditMode
        ? handleEditToggle()
        : isSplitMode
          ? handleSplitToggle()
          : handleDeleteToggle();
      return;
    }
    const randomIndex = Math.floor(Math.random() * totalArticles);
    setCurrentIndex(randomIndex);
    setIsPaused(false);
    addToast(
      `Random article #${randomIndex + 1} / ${totalArticles}`,
      "success-info",
    );
  }, [
    totalArticles,
    addToast,
    isEditMode,
    isSplitMode,
    isDeleteMode,
    handleEditToggle,
    handleSplitToggle,
    handleDeleteToggle,
  ]);

  const handleCopyBody = React.useCallback(() => {
    if (!articles[currentIndex]?.body) return;
    navigator.clipboard
      .writeText(articles[currentIndex].body)
      .then(() => addToast("Text copied to clipboard", "success-info"))
      .catch(() => addToast("Failed to copy text", "error-warning"));
  }, [articles, currentIndex, addToast]);

  const handleSaveSplit = async () => {
    const current = articles[currentIndex];

    if (!newArticleBody.trim()) {
      addToast("New article body is empty", "error-warning");
      return;
    }

    try {
      // Save current article if modified
      if (originalArticle && current.body !== originalArticle.body) {
        await articlesAPI.updateArticle(current._id, {
          title: current.title,
          body: current.body,
          chapter: current.chapter,
          author: current.author,
        });
      }

      // Create new article with proper order
      const newArticle = {
        title: current.title,
        author: current.author,
        chapter: current.chapter,
        body: newArticleBody,
        folder: current.folder,
        contentType: current.contentType,
        lessonId: current.lessonId,
        order: (current.order || 0) + 0.5,
      };

      await articlesAPI.createArticle(newArticle);

      const savedIndex = currentIndex;
      await loadMyArticles();
      setCurrentIndex(savedIndex);

      setIsSplitMode(false);
      setNewArticleBody("");
      setOriginalArticle(null);
      setIsPaused(false);
      addToast("Note created", "success-info");
    } catch {
      addToast("Failed to split article", "error-warning");
    }
  };

  const handleDeleteArticle = async () => {
    const current = articles[currentIndex];
    const savedIndex = currentIndex;
    try {
      await articlesAPI.deleteArticle(current._id);
      await loadMyArticles();

      // Stay on same index, or go to previous if we deleted the last one
      if (savedIndex >= totalArticles - 1) {
        setCurrentIndex(Math.max(0, totalArticles - 2));
      } else {
        setCurrentIndex(savedIndex);
      }

      setIsDeleteMode(false);
      setIsPaused(false);
      addToast("Note deleted", "success-info");
    } catch {
      addToast("Failed to delete article", "error-warning");
    }
  };

  const handleArticleChange = (field, value) => {
    const updatedArticles = [...articles];
    updatedArticles[currentIndex] = {
      ...updatedArticles[currentIndex],
      [field]: value,
    };
    setArticles(updatedArticles);
  };

  const handleSaveEdit = async () => {
    const current = articles[currentIndex];

    if (originalArticle && current.body === originalArticle.body) {
      addToast("No changes to save", "success-info");
      setIsEditMode(false);
      setOriginalArticle(null);
      setIsPaused(false);
      return;
    }

    try {
      await articlesAPI.updateArticle(current._id, {
        title: current.title,
        body: current.body,
        chapter: current.chapter,
        author: current.author,
      });

      const savedIndex = currentIndex;
      await loadMyArticles();
      setCurrentIndex(savedIndex);
      setIsEditMode(false);
      setOriginalArticle(null);
      setIsPaused(false);
      addToast("Note updated", "success-info");
    } catch {
      addToast("Failed to update article", "error-warning");
    }
  };

  const handleJumpToIndex = (index) => {
    if (isEditMode || isSplitMode) {
      isEditMode ? handleEditToggle() : handleSplitToggle();
      return;
    }
    if (index === currentIndex) {
      setResetTrigger((prev) => !prev);
    } else {
      setCurrentIndex(index);
      addToast(`${index + 1} / ${totalArticles}`, "success-info");
    }
    setIsPaused(false);
  };

  const handleFolderChange = (folderName) => {
    if (isEditMode || isSplitMode) {
      isEditMode ? handleEditToggle() : handleSplitToggle();
      return;
    }
    setSelectedFolder(folderName);
    setCurrentIndex(0);
    setIsPaused(false);

    const newUrl = folderName
      ? `/study/notes?folder=${encodeURIComponent(folderName)}`
      : "/study/notes";
    navigate(newUrl);
  };

  const calculateTotalDuration = () => {
    // Calculate remaining time from current index onwards
    const remainingArticles = articles.slice(currentIndex);
    const totalWords = remainingArticles.reduce((sum, article) => {
      return sum + (article.body?.split(" ").length || 0);
    }, 0);

    // Calculate total seconds for all remaining articles
    let totalSeconds = 0;
    remainingArticles.forEach((article) => {
      const wordsCount = article.body?.split(" ").length || 0;
      const articleTime = Math.max(
        5,
        Math.ceil((wordsCount / 200) * 60 * speedMultiplier),
      );
      totalSeconds += articleTime;
    });

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const displayTime =
      hours > 0
        ? `${hours}h${minutes}`
        : minutes > 0
          ? `${minutes}m`
          : `${seconds}s`;
    return { hours, minutes, totalSeconds, totalWords, displayTime };
  };

  const calculateSpeedForTargetTime = () => {
    const remainingArticles = articles.slice(currentIndex);
    const totalWords = remainingArticles.reduce(
      (sum, article) => sum + (article.body?.split(" ").length || 0),
      0,
    );
    const targetSeconds =
      (parseInt(targetHours) || 0) * 3600 + (parseInt(targetMinutes) || 0) * 60;
    if (targetSeconds === 0) return;

    // Calculate time at 1x (base reading speed)
    const timeAt1x = Math.max(
      5 * remainingArticles.length,
      Math.ceil((totalWords / 200) * 60 * 1),
    );
    const timeAt50x = Math.max(
      5 * remainingArticles.length,
      Math.ceil((totalWords / 200) * 60 * 50),
    );

    let calculatedSpeed;
    let toastMessage;

    if (targetSeconds <= timeAt1x) {
      calculatedSpeed = 1;
      toastMessage = "Set to min speed: ";
    } else if (targetSeconds >= timeAt50x) {
      calculatedSpeed = 50;
      toastMessage = "Set to max speed: ";
    } else {
      // Correct formula: speed = targetTime / baseTime
      calculatedSpeed = Math.round(targetSeconds / ((totalWords / 200) * 60));
      toastMessage = "Speed adjusted to ";
    }

    setSpeedMultiplier(calculatedSpeed);
    setShowSpeedMenu(false);
    addToast(`${toastMessage}${calculatedSpeed}x`, "success-info");
    setTargetHours("");
    setTargetMinutes("");
  };

  const { displayTime } = calculateTotalDuration();

  useEffect(() => {
    if (!timerEndTime) {
      setTimerDisplay("");
      return;
    }

    const interval = setInterval(() => {
      const token = localStorage.getItem("token");
      if (!token) {
        setTimerEndTime(null);
        setTimerDisplay("");
        clearInterval(interval);
        return;
      }

      const now = Date.now();
      const remaining = timerEndTime - now;

      if (remaining <= 0) {
        setTimerDisplay("0s");
        return;
      }

      const hours = Math.floor(remaining / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);

      if (hours > 0) {
        setTimerDisplay(`${hours}h${minutes}`);
      } else if (minutes > 0) {
        setTimerDisplay(`${minutes}m${seconds}`);
      } else {
        setTimerDisplay(`${seconds}s`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [timerEndTime, addToast]);

  const setTimer = () => {
    const hours = parseInt(timerHours) || 0;
    const minutes = parseInt(timerMinutes) || 0;
    if (hours === 0 && minutes === 0) return;

    const setAt = Date.now();
    const durationMinutes = hours * 60 + minutes;
    const endTime = setAt + hours * 3600000 + minutes * 60000;
    setTimerEndTime(endTime);
    localStorage.setItem("studyTimer", endTime.toString());
    localStorage.setItem(
      "studyTimerInfo",
      JSON.stringify({ setAt, durationMinutes })
    );
    setShowTimerMenu(false);
    setTimerHours("");
    setTimerMinutes("");

    let timeStr;
    if (hours > 0 && minutes > 0) {
      timeStr = `${hours} ${hours === 1 ? "hour" : "hours"} ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    } else if (hours > 0) {
      timeStr = `${hours} ${hours === 1 ? "hour" : "hours"}`;
    } else {
      timeStr = `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    }
    addToast(`Timer set for ${timeStr}`, "success-info");
  };

  const clearTimer = () => {
    setTimerEndTime(null);
    localStorage.removeItem("studyTimer");
    localStorage.removeItem("studyTimerInfo");
    setTimerDisplay("");
    addToast("Timer cleared", "success-info");
  };

  useEffect(() => {
    const handleTimerCleared = () => {
      const savedTimer = localStorage.getItem("studyTimer");
      if (!savedTimer && timerEndTime) {
        setTimerEndTime(null);
        setTimerDisplay("");
      }
    };

    window.addEventListener("storage", handleTimerCleared);
    const interval = setInterval(handleTimerCleared, 500);
    return () => {
      window.removeEventListener("storage", handleTimerCleared);
      clearInterval(interval);
    };
  }, [timerEndTime]);

  const handleControlsVisibilityChange = () => {};

  useEffect(() => {
    if (!showSpeedMenu && !showTimerMenu) return;
    const handleClickOutside = (e) => {
      const speedMenu = document.querySelector(
        'div[style*="position: absolute"][style*="bottom: 100%"]',
      );
      const speedButton = e.target.closest(
        '.studynotes-random-button[title="Number of times to repeat"]',
      );
      const timerButton = e.target.closest(
        '.studynotes-random-button[title="Set timer"], .studynotes-random-button[title="Clear timer"]',
      );

      if (
        showSpeedMenu &&
        !speedButton &&
        speedMenu &&
        !speedMenu.contains(e.target)
      ) {
        setShowSpeedMenu(false);
      }

      if (
        showTimerMenu &&
        !timerButton &&
        speedMenu &&
        !speedMenu.contains(e.target)
      ) {
        setShowTimerMenu(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showSpeedMenu, showTimerMenu]);

  // Toggle reading mode - immediate hide/show without timer
  const prevReadingMode = React.useRef(globalReadingMode);
  React.useEffect(() => {
    if (globalReadingMode) {
      document.body.classList.add("reading-mode");
    } else {
      document.body.classList.remove("reading-mode");

      // Only add exit effect if we were previously in reading mode
      if (prevReadingMode.current === true) {
        // Delay showing header/footer when exiting reading mode
        document.body.classList.add("reading-mode-exit");
        setTimeout(() => {
          document.body.classList.remove("reading-mode-exit");
        }, 1000);
      }
    }

    prevReadingMode.current = globalReadingMode;

    return () => {
      document.body.classList.remove("reading-mode");
      document.body.classList.remove("reading-mode-exit");
    };
  }, [globalReadingMode]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only handle if no input is focused
      if (
        document.activeElement.tagName === "INPUT" ||
        document.activeElement.tagName === "TEXTAREA"
      ) {
        return;
      }

      if (e.key === "ArrowLeft") {
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        handleNext(false); // No chime on keyboard navigation
      } else if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        togglePause();
      } else if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "PageUp" ||
        e.key === "PageDown"
      ) {
        const articleText = document.querySelector(".articlecard-content p");
        if (!articleText) return;

        const scrollAmount = 100;
        const pageScrollAmount = articleText.clientHeight * 0.8;

        switch (e.key) {
          case "ArrowUp":
            e.preventDefault();
            articleText.scrollTop -= scrollAmount;
            break;
          case "ArrowDown":
            e.preventDefault();
            articleText.scrollTop += scrollAmount;
            break;
          case "PageUp":
            e.preventDefault();
            articleText.scrollTop -= pageScrollAmount;
            break;
          case "PageDown":
            e.preventDefault();
            articleText.scrollTop += pageScrollAmount;
            break;
        }
      }
    };

    const handleWheel = (e) => {
      // Check if mouse is over a dropdown menu first
      const dropdownMenu = e.target.closest(".articlecard-folder-menu");
      if (dropdownMenu) {
        // Allow normal scroll on dropdown menus
        return;
      }

      const articleText = document.querySelector(".articlecard-content p");
      if (!articleText) return;

      // Check if the mouse is over the article content area
      const articleContent = document.querySelector(".articlecard-content");
      if (!articleContent) return;

      const rect = articleContent.getBoundingClientRect();
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      if (
        mouseX >= rect.left &&
        mouseX <= rect.right &&
        mouseY >= rect.top &&
        mouseY <= rect.bottom
      ) {
        e.preventDefault();
        articleText.scrollTop += e.deltaY;
      }
    };

    const handleReadingToggle = () => {
      setReadingModeEnabled((prev) => !prev);
    };

    window.addEventListener("keydown", handleKeyPress);
    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("toggleReading", handleReadingToggle);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("toggleReading", handleReadingToggle);
    };
  }, [handlePrevious, handleNext, togglePause]);

  // Hide cursor after 10 seconds of inactivity
  useEffect(() => {
    const handleMouseMove = () => {
      document.body.classList.remove("hide-cursor");
      if (cursorTimeoutRef.current) {
        clearTimeout(cursorTimeoutRef.current);
      }
      cursorTimeoutRef.current = setTimeout(() => {
        document.body.classList.add("hide-cursor");
      }, 10000);
    };

    window.addEventListener("mousemove", handleMouseMove);
    handleMouseMove();

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.body.classList.remove("hide-cursor");
      if (cursorTimeoutRef.current) {
        clearTimeout(cursorTimeoutRef.current);
      }
    };
  }, []);

  const buildBreadcrumbs = React.useMemo(() => {
    const breadcrumbs = [{ label: "MySpace", path: "/myspace" }];

    // Only add Classes and folder path if we have a selected folder
    if (selectedFolder) {
      breadcrumbs.push({ label: "Classes", path: "/study/lessons" });

      const folderParts = selectedFolder.split(" / ");
      folderParts.forEach((part, index) => {
        const folderPath = folderParts.slice(0, index + 1).join(" / ");
        breadcrumbs.push({
          label: part,
          path: `/study/lessons?folder=${encodeURIComponent(folderPath)}`,
        });
      });

      if (articles[currentIndex]?.chapter) {
        const lessonId =
          articles[currentIndex].lessonId?._id ||
          articles[currentIndex].lessonId;
        breadcrumbs.push({
          label: articles[currentIndex].chapter,
          path: lessonId
            ? buildLessonPath(lessonId)
            : `/study/notes?folder=${encodeURIComponent(selectedFolder)}&type=note`,
        });
      }

      if (typeFromUrl === "note" && articles[currentIndex]?.lessonId) {
        const lessonId =
          articles[currentIndex].lessonId?._id ||
          articles[currentIndex].lessonId;
        breadcrumbs.push({
          label: "Notes",
          path: buildLessonPath(lessonId, { openTakeNotes: true }),
          hideOnMobile: true,
        });
      }

      if (articles[currentIndex]) {
        const article = articles[currentIndex];
        const badge =
          article.contentType === "lesson"
            ? "L"
            : article.contentType === "flashcard"
              ? "F"
              : "N";
        const badgeClass =
          article.contentType === "lesson"
            ? "studynotes-breadcrumb-badge-lesson"
            : article.contentType === "flashcard"
              ? "studynotes-breadcrumb-badge-flashcard"
              : "studynotes-breadcrumb-badge-note";

        const text = article.chapter || article.title;
        const maxLength = 25;
        const truncatedText = text && text.length > maxLength ? text.substring(0, maxLength) + '...' : text;

        breadcrumbs.push({
          label: (
            <span className="studynotes-breadcrumb-current" title={text}>
              <span className={`studynotes-breadcrumb-badge ${badgeClass}`}>
                {badge}
              </span>
              <span className="studynotes-breadcrumb-label">{truncatedText}</span>
            </span>
          ),
          path: null,
        });
      }
    } else {
      // When no folder is selected, just show "Notes"
      breadcrumbs.push({
        label: "Notes",
        path: null,
      });
    }

    return breadcrumbs;
  }, [selectedFolder, articles, currentIndex, typeFromUrl]);

  const handleCustomExit = React.useCallback(() => {
    if (lessonIdFromUrl && folderFromUrl) {
      navigate("/all-notes");
    } else {
      navigate("/myspace");
    }
  }, [lessonIdFromUrl, folderFromUrl, navigate]);

  if (loading) {
    return (
      <StudyLayout
        breadcrumbs={<Breadcrumbs path={buildBreadcrumbs} />}
        buttons={
          <NavigationButtons
            customExitHandler={handleCustomExit}
            hiddenButtons={
              isSplitMode
                ? ["myspace", "mycontent", "create", "community"]
                : []
            }
          />
        }
      >
        <Spinner visible={true} />
      </StudyLayout>
    );
  }

  if (articles.length === 0) {
    return (
      <StudyLayout
        breadcrumbs={<Breadcrumbs path={buildBreadcrumbs} />}
        buttons={
          globalReadingMode ? null : (
            <NavigationButtons customExitHandler={handleCustomExit} />
          )
        }
      >
        <div className="studynotes-empty">No notes available</div>
      </StudyLayout>
    );
  }

  return (
    <StudyLayout
      breadcrumbs={<Breadcrumbs path={buildBreadcrumbs} />}
      buttons={
        <NavigationButtons
          customExitHandler={handleCustomExit}
          hiddenButtons={
            isSplitMode
              ? ["myspace", "mycontent", "create", "community"]
              : []
          }
        />
      }
    >
      {/* <div className="myspace-watermark">
        <div className="header-title-watermark">
          <h1 className="header-logo-watermark">Study Notes</h1>
        </div>
      </div> */}
      <main className="studynotes-main">
        <div className={`studynotes-middle`}>
          {isSplitMode ? (
            <div className="split-container">
              <div className="split-left">
                <textarea
                  ref={editTextareaRef}
                  value={articles[currentIndex]?.body || ""}
                  onChange={(e) => handleArticleChange("body", e.target.value)}
                  className="split-textarea"
                  maxLength={CONTENT_LIMITS.noteBody}
                />
              </div>
              <div className="split-right">
                <textarea
                  value={newArticleBody}
                  onChange={(e) => setNewArticleBody(e.target.value)}
                  className="split-textarea"
                  placeholder="New article body..."
                  maxLength={CONTENT_LIMITS.noteBody}
                />
              </div>
            </div>
          ) : (
            <ArticleCard
              article={articles[currentIndex]}
              currentIndex={currentIndex}
              totalArticles={totalArticles}
              onPrevious={handlePrevious}
              onNext={handleNext}
              isPaused={isPaused}
              onResetTimer={() => setIsPaused(false)}
              resetTrigger={resetTrigger}
              onTogglePause={togglePause}
              onJumpToIndex={handleJumpToIndex}
              onVisibilityChange={handleControlsVisibilityChange}
              folders={[]}
              selectedFolder={selectedFolder}
              onFolderChange={handleFolderChange}
              isEditMode={isEditMode}
              onArticleChange={handleArticleChange}
              maxBodyLength={CONTENT_LIMITS.noteBody}
              speedMultiplier={speedMultiplier}
              progressLineEnabled={progressLineEnabled}
            />
          )}

          {/* Buttons row under progress bar */}
          {totalArticles > 0 && (
            <div
              className={`studynotes-buttons-row ${globalReadingMode ? "reading-mode-fade" : ""}`}
              style={{ position: "relative" }}
            >
              <div className="studynotes-button-group">
                <button
                  className="studynotes-random-button"
                  onClick={() => {
                    toggleReadingMode();
                    if (!readingModeEnabled) {
                      document.documentElement.requestFullscreen();
                    } else if (document.fullscreenElement) {
                      document.exitFullscreen();
                    }
                    addToast(
                      `Reading mode ${globalReadingMode ? "OFF" : "ON"}`,
                      "success-info",
                    );
                  }}
                  title="Reading mode"
                >
                  <Eye size={18} />
                </button>
                <button
                  className="studynotes-random-button"
                  onClick={() => {
                    const newValue = !chimeEnabled;
                    setChimeEnabled(newValue);
                    localStorage.setItem(
                      "chimeEnabled",
                      JSON.stringify(newValue),
                    );
                    addToast(
                      `Chime ${chimeEnabled ? "OFF" : "ON"}`,
                      "success-info",
                    );
                  }}
                  title={chimeEnabled ? "Chime ON" : "Chime OFF"}
                >
                  {chimeEnabled ? <Bell size={18} /> : <BellOff size={18} />}
                </button>
                <button
                  className="studynotes-random-button"
                  onClick={() => {
                    const newValue = !progressLineEnabled;
                    setProgressLineEnabled(newValue);
                    localStorage.setItem(
                      "progressLineEnabled",
                      JSON.stringify(newValue),
                    );
                    addToast(
                      `Progress line ${progressLineEnabled ? "OFF" : "ON"}`,
                      "success-info",
                    );
                  }}
                  title={
                    progressLineEnabled ? "Progress line ON" : "Progress line OFF"
                  }
                >
                  <Minus
                    size={18}
                    style={{ opacity: progressLineEnabled ? 1 : 0.3 }}
                  />
                </button>
                <div className="studynotes-action-wrapper" ref={bookmarkActionRef}>
                  {isBookmarkMode ? (
                    <div className="studynotes-action-wrapper">
                      <button
                        className="studynotes-random-button"
                        onClick={() => setIsBookmarkMode(false)}
                        title="Cancel"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                      <button
                        className="studynotes-random-button studynotes-floating-save studynotes-floating-save-priority studynotes-bookmark-confirm-button"
                        onClick={handleCreateBookmark}
                        title="Create Bookmark"
                      >
                        <span className="studynotes-bookmark-create-icon">
                          <Plus size={10} className="studynotes-bookmark-plus-icon" />
                          <Bookmark size={16} />
                        </span>
                      </button>
                    </div>
                  ) : bookmark?.articleId === articles[currentIndex]?._id ? (
                    <button
                      className="studynotes-random-button"
                      onClick={handleDeleteBookmark}
                      title="Remove bookmark"
                      disabled={loadingBookmark}
                    >
                      <Bookmark size={18} fill="#3b82f6" color="#3b82f6" />
                    </button>
                  ) : (
                    <button
                      className="studynotes-random-button"
                      onClick={handleBookmarkClick}
                      title="Bookmark"
                      disabled={loadingBookmark}
                    >
                      <Bookmark
                        size={18}
                        color={bookmark ? "#3b82f6" : undefined}
                        fill={bookmark ? "#3b82f6" : "none"}
                      />
                    </button>
                  )}
                </div>
              </div>
              <div style={{ position: "relative" }}>
                <button
                  className="studynotes-random-button"
                  onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                  title="Number of times to repeat"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "0px",
                    padding: "4px",
                  }}
                >
                  <Gauge size={14} />
                  <span style={{ fontSize: "10px", lineHeight: "1" }}>
                    {speedMultiplier === 5
                      ? 3
                      : speedMultiplier === 15
                        ? 10
                        : speedMultiplier}
                    x
                  </span>
                </button>
                {showSpeedMenu && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      marginBottom: "8px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      zIndex: 1000,
                      alignItems: "center",
                      background: "var(--color-bg-primary)",
                      padding: "6px",
                      borderRadius: "4px",
                    }}
                  >
                    {[1, 3, 10].map((displaySpeed) => {
                      const actualSpeed =
                        displaySpeed === 3
                          ? 5
                          : displaySpeed === 10
                            ? 15
                            : displaySpeed;
                      return (
                        <button
                          key={displaySpeed}
                          onClick={() => {
                            setSpeedMultiplier(actualSpeed);
                            setShowSpeedMenu(false);
                            addToast(`Speed: ${displaySpeed}x`, "success-info");
                          }}
                          style={{
                            padding: "6px 12px",
                            background:
                              speedMultiplier === actualSpeed
                                ? "rgba(220, 220, 220, 0.3)"
                                : "transparent",
                            border: "1px solid var(--color-border)",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "var(--color-text-primary)",
                            transition: "background 0.2s",
                            minWidth: "60px",
                          }}
                          onMouseEnter={(e) => {
                            if (speedMultiplier !== actualSpeed) {
                              e.target.style.background =
                                "rgba(220, 220, 220, 0.3)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (speedMultiplier !== actualSpeed) {
                              e.target.style.background = "transparent";
                            }
                          }}
                        >
                          {displaySpeed}x
                        </button>
                      );
                    })}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        padding: "6px",
                        background: "var(--color-bg-primary)",
                        borderRadius: "4px",
                        alignItems: "center",
                        width: "fit-content",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "2px",
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="number"
                          placeholder="h"
                          value={targetHours}
                          onChange={(e) => setTargetHours(e.target.value)}
                          onKeyPress={(e) =>
                            e.key === "Enter" && calculateSpeedForTargetTime()
                          }
                          style={{
                            width: "30px",
                            padding: "4px",
                            background: "var(--color-bg-primary)",
                            border: "1px solid var(--color-border)",
                            borderRadius: "4px",
                            color: "var(--color-text-primary)",
                            fontSize: "12px",
                            textAlign: "center",
                          }}
                        />
                        <span
                          style={{
                            color: "var(--color-text-primary)",
                            fontSize: "12px",
                          }}
                        >
                          :
                        </span>
                        <input
                          type="number"
                          placeholder="m"
                          value={targetMinutes}
                          onChange={(e) => setTargetMinutes(e.target.value)}
                          onKeyPress={(e) =>
                            e.key === "Enter" && calculateSpeedForTargetTime()
                          }
                          style={{
                            width: "30px",
                            padding: "4px",
                            background: "var(--color-bg-primary)",
                            border: "1px solid var(--color-border)",
                            borderRadius: "4px",
                            color: "var(--color-text-primary)",
                            fontSize: "12px",
                            textAlign: "center",
                          }}
                        />
                      </div>
                      <button
                        onClick={calculateSpeedForTargetTime}
                        style={{
                          padding: "4px 8px",
                          background: "transparent",
                          border: "1px solid var(--color-border)",
                          borderRadius: "4px",
                          color: "var(--color-text-primary)",
                          fontSize: "12px",
                          cursor: "pointer",
                          width: "100%",
                        }}
                      >
                        Set
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button
                className="studynotes-random-button"
                onClick={() => {
                  setShowDurationInfo(true);
                  setTimeout(() => setShowDurationInfo(false), 5000);
                }}
                title="Total duration"
                style={{ position: "relative" }}
              >
                <span style={{ fontSize: "11px", fontWeight: "600" }}>
                  {displayTime}
                </span>
                {showDurationInfo && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      left: "0",
                      marginBottom: "12px",
                      background: "var(--color-bg-primary)",
                      border: "1px solid var(--color-border)",
                      padding: "6px",
                      borderRadius: "4px",
                      zIndex: 1000,
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "var(--color-text-primary)",
                      whiteSpace: "nowrap",
                      outline: "none",
                      pointerEvents: "none",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div>
                      {totalArticles - currentIndex} articles left at{" "}
                      {speedMultiplier === 5 ? 3 : speedMultiplier === 15 ? 10 : speedMultiplier}x
                    </div>
                  </div>
                )}
              </button>
              <input
                type="number"
                min="1"
                max={totalArticles}
                value={jumpIndex}
                onChange={(e) => setJumpIndex(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    const index = parseInt(jumpIndex) || 1;
                    if (index >= 1 && index <= totalArticles) {
                      setCurrentIndex(index - 1);
                      setIsPaused(false);
                      addToast(`${index} / ${totalArticles}`, "success-info");
                      e.target.blur();
                    }
                  }
                }}
                onFocus={(e) => {
                  e.target.select();
                }}
                onBlur={() => setJumpIndex("")}
                placeholder={currentIndex + 1}
                className="studynotes-control-input"
                title="Click to enter a new index"
              />
              <button
                className="studynotes-random-button"
                onClick={togglePause}
                title={isPaused ? "Resume" : "Pause"}
              >
                {isPaused ? <Play size={18} /> : <Pause size={18} />}
              </button>
              <div
                className="studynotes-total-display"
                title="Total articles in current folder"
              >
                {totalArticles}
              </div>
              <button
                className="studynotes-random-button"
                onClick={handleRandom}
                title="Random Article"
              >
                <Shuffle size={18} />
              </button>
              <div className="studynotes-button-group">
                <div style={{ position: "relative" }}>
                  <button
                    className="studynotes-random-button"
                    onClick={() =>
                      timerEndTime
                        ? clearTimer()
                        : setShowTimerMenu(!showTimerMenu)
                    }
                    title={timerEndTime ? "Clear timer" : "Set timer"}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "0px",
                      padding: "4px",
                    }}
                  >
                    {timerEndTime ? (
                      <>
                        <Timer size={14} />
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: "400",
                            lineHeight: "1",
                          }}
                        >
                          {timerDisplay}
                        </span>
                      </>
                    ) : (
                      <Timer size={18} />
                    )}
                  </button>
                  {showTimerMenu && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: "100%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        marginBottom: "8px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        zIndex: 1000,
                        background: "var(--color-bg-primary)",
                        padding: "6px",
                        borderRadius: "4px",
                        border: "1px solid var(--color-border)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "2px",
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="number"
                          placeholder="h"
                          value={timerHours}
                          onChange={(e) => setTimerHours(e.target.value)}
                          onKeyPress={(e) => e.key === "Enter" && setTimer()}
                          style={{
                            width: "30px",
                            padding: "4px",
                            background: "var(--color-bg-primary)",
                            border: "1px solid var(--color-border)",
                            borderRadius: "4px",
                            color: "var(--color-text-primary)",
                            fontSize: "12px",
                            textAlign: "center",
                          }}
                        />
                        <span
                          style={{
                            color: "var(--color-text-primary)",
                            fontSize: "12px",
                          }}
                        >
                          :
                        </span>
                        <input
                          type="number"
                          placeholder="m"
                          value={timerMinutes}
                          onChange={(e) => setTimerMinutes(e.target.value)}
                          onKeyPress={(e) => e.key === "Enter" && setTimer()}
                          style={{
                            width: "30px",
                            padding: "4px",
                            background: "var(--color-bg-primary)",
                            border: "1px solid var(--color-border)",
                            borderRadius: "4px",
                            color: "var(--color-text-primary)",
                            fontSize: "12px",
                            textAlign: "center",
                          }}
                        />
                      </div>
                      <button
                        onClick={setTimer}
                        style={{
                          padding: "4px 8px",
                          background: "transparent",
                          border: "1px solid var(--color-border)",
                          borderRadius: "4px",
                          color: "var(--color-text-primary)",
                          fontSize: "12px",
                          cursor: "pointer",
                          width: "100%",
                        }}
                      >
                        Set
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="studynotes-ced-group">
              <div className="studynotes-action-wrapper">
                {isSplitMode ? (
                  <div className="studynotes-action-wrapper">
                    <button
                      className="studynotes-random-button"
                      onClick={handleSplitToggle}
                      title="Cancel"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                    <button
                      className="studynotes-random-button studynotes-floating-save studynotes-floating-save-priority"
                      onClick={handleSaveSplit}
                      title="Save"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                        <polyline points="17 21 17 13 7 13 7 21"></polyline>
                        <polyline points="7 3 7 8 15 8"></polyline>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button
                    className="studynotes-random-button"
                    onClick={handleSplitToggle}
                    title="Split Article"
                  >
                    <Scissors size={18} />
                  </button>
                )}
              </div>
              <button
                className="studynotes-random-button"
                onClick={handleCopyBody}
                title="Copy Text"
              >
                <Copy size={18} />
              </button>
              <div className="studynotes-action-wrapper">
                {isEditMode ? (
                  <div className="studynotes-action-wrapper">
                    <button
                      className="studynotes-random-button"
                      onClick={handleEditToggle}
                      title="Cancel"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                    <button
                      className="studynotes-random-button studynotes-floating-save"
                      onClick={handleSaveEdit}
                      title="Save"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                        <polyline points="17 21 17 13 7 13 7 21"></polyline>
                        <polyline points="7 3 7 8 15 8"></polyline>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button
                    className="studynotes-random-button home-edit-pulse"
                    onClick={handleEditToggle}
                    title="Edit Article"
                  >
                    <Pencil size={18} />
                  </button>
                )}
              </div>
              <div className="studynotes-action-wrapper">
                {isDeleteMode ? (
                  <div className="studynotes-action-wrapper">
                    <button
                      className="studynotes-random-button"
                      onClick={handleDeleteToggle}
                      title="Cancel"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                    <button
                      className="studynotes-random-button studynotes-floating-save"
                      onClick={handleDeleteArticle}
                      title="Confirm Delete"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button
                    className="studynotes-random-button"
                    onClick={handleDeleteToggle}
                    title="Delete Article"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
              </div>
            </div>
          )}
        </div>

        {/* Persistent reading mode button */}
        {globalReadingMode && (
          <button
            className="reading-mode-toggle-persistent"
            onClick={() => {
              toggleReadingMode();
              if (document.fullscreenElement) {
                document.exitFullscreen();
              }
              addToast("Reading mode OFF", "success-info");
            }}
            title="Exit reading mode"
          >
            <X size={18} strokeWidth={2} />
          </button>
        )}
      </main>
    </StudyLayout>
  );
};

export default StudyNotes;
